// @ts-strict-ignore
import React from 'react';
import {
  TextInput as NativeTextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ScrollView,
} from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor, FeatureTheme } from '@styleseat/brand';
import SearchInput from '../SearchInput';
import {
  Absolute,
  Stack,
  TextInput,
  BodyText,
  Button,
  Touchable,
} from '../../../ui';
import { MapsPinIcon } from '../../../ui/Icons/MapsPinIcon';
import { MapsLocationIcon } from '../../../ui/Icons/MapsLocationIcon';
import QuerySuggestionsList from '../QuerySuggestionsList';
import {
  IQuerySuggestion,
  IQuerySuggestionList,
  QueryResultType,
} from './types';
import { LOADING_LOCATION_COPY, USE_CURRENT_LOCATION_COPY } from './constants';
import { BasicCircleStarIcon, BasicCircleXIcon } from '../../../ui/Icons';
import { ExpandableQuerySuggestion } from './ExpandableQuerySuggestion';
import { clickable } from '../../../../styles';

type SearchFormProps = {
  queryValue: string;
  onChangeQueryValue: (value: string) => void;
  locationValue: string;
  locationResultsValue: IQuerySuggestionList[];
  locationIsLoading: boolean;
  showLocationList: boolean;
  searchResultsValue: IQuerySuggestionList[];
  showSubItems?: boolean;
  onSearchItemExpand?: (item: IQuerySuggestion) => void;
  onLocationFocus: () => void;
  onSearchItemSelect: (data: IQuerySuggestion) => void;
  onChangeLocationValue: (value: string) => void;
  onSelectLocation: (value: IQuerySuggestion) => void;
  onSearchPress: (query: string) => void;
  onFindLocation: () => void;
  onClearLocationValue: () => void;
  onClearQueryValue: () => void;
  nothingIsFocused: boolean;
  locationIsFocused: boolean;
  onQueryFocus: () => void;
  onQueryBlur: () => void;
  onLocationBlur: () => void;
  /**
   * This is used in place of onBlur for taking the first location suggestion
   * because onBlur was preventing the onSelectLocation from firing.
   */
  onUnfocusLocationResults: () => void;
  error: React.ReactNode;
  searchInputRef: React.Ref<SearchInput>;
  locationInputRef: React.Ref<NativeTextInput>;
  showIncentivesStar: boolean;
};

const SearchForm: React.FCWithChildren<SearchFormProps> = ({
  onChangeQueryValue,
  queryValue,
  locationValue,
  locationResultsValue,
  locationIsLoading,
  showLocationList,
  searchResultsValue,
  showSubItems,
  onSearchItemExpand,
  onLocationFocus,
  onSearchItemSelect,
  onChangeLocationValue,
  onSelectLocation,
  onSearchPress,
  onFindLocation,
  onClearLocationValue,
  onClearQueryValue,
  nothingIsFocused,
  onQueryBlur,
  onQueryFocus,
  onLocationBlur,
  onUnfocusLocationResults,
  searchInputRef,
  locationInputRef,
  locationIsFocused,
  error,
  showIncentivesStar,
}) => {
  const handleSearchPress = () => {
    onSearchPress(queryValue);
  };

  const handleKeyPress = (key: string) => {
    if (key !== 'Enter') {
      return;
    }

    handleSearchPress();
  };

  const handleSearchButton = () => (
    <Absolute
      color={BrandColor.WhiteWash}
      left={0}
      right={0}
      bottom={0}
    >
      <Box padding={12}>
        <Button
          type="submit"
          testID="search-popover-search-button"
          onClick={() => handleSearchPress()}
        >
          Search
        </Button>
      </Box>
    </Absolute>
  );

  return (
    <TouchableWithoutFeedback onPress={onUnfocusLocationResults}>
      {/** This TouchableWithoutFeedback ^ is used for capturing the event
     * of when the locationList loses focus. This is needed because onBlur
     * was masking nested events.
    */}
      <Box
        paddingBottom={nothingIsFocused ? 48 : 0}
        flex={1}
        style={clickable}
      >
        <Box
          padding={12}
          paddingBottom={20}
          flex={1}
        >
          <form
            style={{ display: 'inline', margin: 0 }}
            action=""
            onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              handleSearchPress();
            }}
          >
            <button
              type="submit"
              style={{
                visibility: 'hidden', position: 'absolute', left: -10000, top: -10000,
              }}
            >
              Search
            </button>
            <Stack
              paddingBottom={8}
              space={8}
            >
              <SearchInput
                autofocus
                value={queryValue}
                onChangeText={onChangeQueryValue}
                onClearQueryValue={onClearQueryValue}
                onFocus={onQueryFocus}
                onBlur={onQueryBlur}
                onKeyPress={handleKeyPress}
                ref={searchInputRef}
                onSearchPress={() => handleSearchPress()}
                closeIcon={<BasicCircleXIcon width={16} height={16} color={BrandColor.Ash} />}
              />
              {/* Location input */}
              <Inline
                alignItems="center"
                color={BrandColor.WhiteWash}
                borderWidth={1}
                borderStyle="solid"
                borderColor={locationIsFocused ? BrandColor.BackInBlack : BrandColor.WhiteWash}
                borderRadius={20}
                minHeight={40}
              >
                <Box paddingX={8}>
                  <MapsPinIcon
                    width={20}
                    height={20}
                  />
                </Box>
                <TextInput
                  accentColor="rgba(0, 0, 0, 0)"
                  style={{
                    paddingLeft: 2,
                    fontSize: 16,
                    backgroundColor: BrandColor.WhiteWash,
                    flexGrow: 1,
                    flexShrink: 1,
                    height: 30,
                  }}
                  value={locationValue}
                  onChangeText={onChangeLocationValue}
                  placeholder="City or zip code"
                  onFocus={onLocationFocus}
                  onBlur={onLocationBlur}
                  onKeyPress={event => handleKeyPress(event.nativeEvent.key)}
                  testID="search-popover-location"
                  ref={locationInputRef}
                  type="search"
                />
                {locationValue?.length > 0 ? (
                  <Touchable onClick={onClearLocationValue}>
                    <Box paddingX={8} color={BrandColor.Transparent}>
                      <BasicCircleXIcon width={16} height={16} color={BrandColor.Ash} />
                    </Box>
                  </Touchable>
                ) : (
                  <Box
                    paddingX={8}
                    color={BrandColor.Transparent}
                    minHeight={20}
                    minWidth={20}
                  />
                )}
              </Inline>
            </Stack>
          </form>
          {error}

          {showIncentivesStar && (
            <Inline
              space={4}
              paddingTop={8}
              paddingBottom={8}
              alignItems="center"
              testID="search-form-incentives-banner"
            >
              <BasicCircleStarIcon
                width={16}
                height={16}
                color={FeatureTheme.DiscountsColor}
              />
              <BodyText>
                Search for any service to see eligible discounts.
              </BodyText>
            </Inline>
          )}

          {/** show location results */}
          {showLocationList && (
            <ScrollView>
              {!error && !!locationResultsValue?.length && (
                <TouchableOpacity onPress={onFindLocation}>
                  <Inline
                    borderColor={BrandColor.WhiteWash}
                    borderBottomWidth={1}
                    borderStyle="solid"
                    paddingY={16}
                    justifyContent="space-between"
                  >
                    <Inline
                      space={4}
                      justifyContent="space-around"
                      paddingLeft={12}
                    >
                      <MapsLocationIcon
                        width={20}
                        height={20}
                      />
                      <BodyText>
                        {locationIsLoading ? LOADING_LOCATION_COPY : USE_CURRENT_LOCATION_COPY}
                      </BodyText>
                    </Inline>
                  </Inline>
                </TouchableOpacity>
              )}
              {/** location results */}
              <QuerySuggestionsList
                suggestions={locationResultsValue}
                onSelect={onSelectLocation}
              />
            </ScrollView>
          )}
          {/** end location suggestions */}

          {/** show search results */}
          {!showLocationList && (
            showSubItems ? (
              <QuerySuggestionsList
                suggestions={searchResultsValue?.length > 0 ? searchResultsValue : []}
                onSelect={onSearchItemSelect}
                renderSuggestion={(item: IQuerySuggestion, suggestionText: React.ReactElement) => (
                  item.section === QueryResultType.PopularService ? (
                    <ExpandableQuerySuggestion
                      subItems={item.subItems}
                      item={item}
                      onItemSelect={onSearchItemSelect}
                      onSubItemSelect={onSearchItemSelect}
                      onExpand={onSearchItemExpand}
                      suggestionText={suggestionText}
                    />
                  ) : (
                    <Touchable onClick={() => onSearchItemSelect(item)} testID={item.testId}>
                      <Inline
                        borderColor={BrandColor.WhiteWash}
                        borderBottomWidth={1}
                        borderStyle="solid"
                        paddingY={16}
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <BodyText>{suggestionText}</BodyText>
                      </Inline>
                    </Touchable>
                  )
                )}
              />
            ) : (
              <QuerySuggestionsList
                suggestions={searchResultsValue?.length > 0 ? searchResultsValue : []}
                onSelect={onSearchItemSelect}
              />
            )
          )}
        </Box>

        {handleSearchButton()}

      </Box>
    </TouchableWithoutFeedback>
  );
};

export default SearchForm;
